import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Typography } from '@mui/material'
import { OnboardPage } from '../../../helpers/onboard-helper'
import { getOnboardingStepPoints } from '../../../helpers/onboarding-points'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../store'
import { selectSocialLinks, setSocialLinks } from '../../../store/onboardSlice'
import { OnboardSocialRefMethods, SocialLinksState } from '../../../types'
import OnboardTitleTypography from './OnboardTitleTypography'
import { OnboardRequestTextField } from './styled/OnboardRequest.styled'
import { OnboardSocialWarper } from './styled/OnboardSocial.styled'

interface OnboardSocialProps {
  title?: string
  isEditPreference?: boolean
  alignItems?: 'center' | 'right' | 'left'
  setNextButtonDisable?: (disable: boolean) => void // Optional validation callback
}

const OnboardSocial = forwardRef<OnboardSocialRefMethods, OnboardSocialProps>(
  function OnboardSocial(
    { title, isEditPreference = false, alignItems = 'center', setNextButtonDisable },
    ref,
  ): JSX.Element {
    const { dispatch } = useAppUtilities()
    const socialLinks = useAppSelector(selectSocialLinks)
    const [socialLinksData, setSocialLinksData] =
      useState<SocialLinksState>(socialLinks)

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { id, value } = event.target
      setSocialLinksData((prev) => ({ ...prev, [id]: value }))
    }

    // Validation: require at least 1 social handle
    useEffect(() => {
      if (isEditPreference || !setNextButtonDisable) return
      
      const hasAtLeastOneLink = !!(
        socialLinksData.facebook?.trim() ||
        socialLinksData.instagram?.trim() ||
        socialLinksData.x?.trim() ||
        socialLinksData.tiktok?.trim()
      )
      setNextButtonDisable(!hasAtLeastOneLink)
    }, [socialLinksData, isEditPreference, setNextButtonDisable])

    const getPayload = (): SocialLinksState => {
      dispatch(setSocialLinks(socialLinksData))
      return socialLinksData
    }

    useImperativeHandle(ref, () => ({ getPayload }))

    return (
      <>
        {title && (
          <OnboardTitleTypography
            title={title}
            subTitle=""
            isEditPreference={isEditPreference}
            alignItems={alignItems}
            points={getOnboardingStepPoints(OnboardPage.SOCIAL_MEDIA)}
          />
        )}
        {!isEditPreference && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 2 }}
          >
            Optional — you can skip this step
          </Typography>
        )}

        <OnboardSocialWarper>
          <OnboardRequestTextField
            disabled={isEditPreference}
            id="facebook"
            label="Facebook"
            value={socialLinksData.facebook}
            onChange={handleChange}
          />
          <OnboardRequestTextField
            disabled={isEditPreference}
            id="instagram"
            label="Instagram"
            value={socialLinksData.instagram}
            onChange={handleChange}
          />
          <OnboardRequestTextField
            disabled={isEditPreference}
            id="x"
            label="X (formerly Twitter)"
            value={socialLinksData.x}
            onChange={handleChange}
          />
          <OnboardRequestTextField
            disabled={isEditPreference}
            id="tiktok"
            label="Tiktok"
            value={socialLinksData.tiktok}
            onChange={handleChange}
          />
        </OnboardSocialWarper>
      </>
    )
  },
)

export default OnboardSocial
